import React, { useEffect, useRef, useState, useCallback } from "react";
// Note: useCallback kept because SecretMenuProvider uses it for triggerSecretMenu
import { motion, AnimatePresence } from "framer-motion";
import { Gamepad2, Music2, X, ChevronRight } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Hook: useShakeDetection
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// Hook: useShakeDetection
//
// Uses `acceleration` (gravity-free) not `accelerationIncludingGravity` so
// slow tilts don't false-trigger.
//
// iOS 13+ requires a user-gesture permission request. We defer it to the first
// touchstart so no prompt appears on page load.
// ─────────────────────────────────────────────────────────────────────────────
export function useShakeDetection(onShake, { threshold = 15, shakeCount = 4, cooldown = 300 } = {}) {
  const lastXYZ    = useRef(null);
  const count      = useRef(0);
  const lastHit    = useRef(0);
  const resetTimer = useRef(null);
  const listening  = useRef(false);
  const onShakeRef = useRef(onShake);
  useEffect(() => { onShakeRef.current = onShake; }, [onShake]);

  useEffect(() => {
    const handleMotion = (e) => {
      // Prefer gravity-free; fall back to raw if unavailable
      const acc = e.acceleration?.x != null ? e.acceleration : e.accelerationIncludingGravity;
      if (!acc) return;
      const { x = 0, y = 0, z = 0 } = acc;

      if (!lastXYZ.current) { lastXYZ.current = { x, y, z }; return; }

      const delta =
        Math.abs(x - lastXYZ.current.x) +
        Math.abs(y - lastXYZ.current.y) +
        Math.abs(z - lastXYZ.current.z);
      lastXYZ.current = { x, y, z };

      if (delta > threshold) {
        const now = Date.now();
        if (now - lastHit.current > cooldown) {
          count.current  += 1;
          lastHit.current = now;
          clearTimeout(resetTimer.current);
          resetTimer.current = setTimeout(() => { count.current = 0; }, 1500);
        }
        if (count.current >= shakeCount) {
          count.current = 0;
          clearTimeout(resetTimer.current);
          onShakeRef.current();
        }
      }
    };

    const startListening = () => {
      if (listening.current) return;
      listening.current = true;
      window.addEventListener("devicemotion", handleMotion, { passive: true });
    };

    if (
      typeof DeviceMotionEvent !== "undefined" &&
      typeof DeviceMotionEvent.requestPermission === "function"
    ) {
      // iOS 13+: gate behind first user touch so no cold permission prompt
      const onTouch = async () => {
        try {
          const perm = await DeviceMotionEvent.requestPermission();
          if (perm === "granted") startListening();
        } catch { /* denied — shake silently disabled */ }
      };
      window.addEventListener("touchstart", onTouch, { once: true });
    } else {
      startListening(); // Android / desktop — no permission needed
    }

    return () => {
      window.removeEventListener("devicemotion", handleMotion);
      clearTimeout(resetTimer.current);
    };
  }, [threshold, shakeCount, cooldown]);
}

// ─────────────────────────────────────────────────────────────────────────────
// Vibration helper
// ─────────────────────────────────────────────────────────────────────────────
function vibrate(pattern) {
  if (navigator.vibrate) navigator.vibrate(pattern);
}

// ─────────────────────────────────────────────────────────────────────────────
// Context — Navbar reads triggerSecretMenu from here
// ─────────────────────────────────────────────────────────────────────────────
export const SecretMenuContext = React.createContext({ triggerSecretMenu: () => {} });

// ─────────────────────────────────────────────────────────────────────────────
// SecretMenuProvider
// ─────────────────────────────────────────────────────────────────────────────
export default function SecretMenuProvider({ children, onNavigate }) {
  const [open, setOpen] = useState(false);

  const triggerSecretMenu = useCallback(() => {
    vibrate([80, 40, 80, 40, 120]);
    setOpen(true);
  }, []);

  // Shake also triggers the menu (mobile bonus path)
  useShakeDetection(triggerSecretMenu, { threshold: 15, shakeCount: 4, cooldown: 300 });

  return (
    <SecretMenuContext.Provider value={{ triggerSecretMenu }}>
      {children}
      <SecretMenuDrawer
        open={open}
        onClose={() => setOpen(false)}
        onNavigate={onNavigate}
      />
    </SecretMenuContext.Provider>
  );
}
function SecretMenuDrawer({ open, onClose, onNavigate }) {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  const menuItems = [
    {
      id: "game",
      icon: Gamepad2,
      label: "Arcade",
      sub: "3 games to play",
      // Uses --secondary (soft purple) from the design system
      iconColor: "hsl(var(--secondary))",
      iconBg: "hsl(var(--secondary) / 0.12)",
      borderHover: "hsl(var(--secondary) / 0.35)",
      bgHover: "hsl(var(--secondary) / 0.06)",
    },
    {
      id: "music",
      icon: Music2,
      label: "Music Player",
      sub: "Background vibes",
      // Uses --accent (emerald) from the design system
      iconColor: "hsl(var(--accent))",
      iconBg: "hsl(var(--accent) / 0.12)",
      borderHover: "hsl(var(--accent) / 0.35)",
      bgHover: "hsl(var(--accent) / 0.06)",
    },
  ];

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop — matches .glass backdrop pattern */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 998,
              background: "hsl(var(--background) / 0.7)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}
          />

          {/* Drawer shell — uses .glass pattern from CSS */}
          <motion.div
            key="drawer"
            initial={isMobile ? { y: "100%", opacity: 0 } : { y: -16, opacity: 0, scale: 0.97 }}
            animate={isMobile ? { y: 0, opacity: 1 } : { y: 0, opacity: 1, scale: 1 }}
            exit={isMobile ? { y: "100%", opacity: 0 } : { y: -16, opacity: 0, scale: 0.97 }}
            transition={{ type: "spring", damping: 30, stiffness: 340 }}
            style={{
              position: "fixed",
              zIndex: 999,
              ...(isMobile
                ? { bottom: 0, left: 0, right: 0, borderRadius: "20px 20px 0 0" }
                : { top: 68, right: 16, width: 288, borderRadius: "var(--radius, 12px)" }),
              // Glass morphism — mirrors .glass utility exactly
              background: "hsl(var(--glass-bg))",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              border: "1px solid hsl(var(--glass-border))",
              // Glow — mirrors --shadow-primary
              boxShadow: "var(--shadow-lg), 0 0 40px hsl(var(--glow-primary))",
              overflow: "hidden",
              paddingBottom: "env(safe-area-inset-bottom, 0px)",
            }}
          >
            {/* Drag handle — mobile only */}
            {isMobile && (
              <div
                style={{
                  width: 32,
                  height: 3,
                  borderRadius: 999,
                  background: "hsl(var(--muted-foreground) / 0.3)",
                  margin: "10px auto 0",
                }}
              />
            )}

            {/* Gradient accent line at top — mirrors hero section style */}
            <div
              style={{
                height: 1,
                background: "var(--gradient-primary)",
                opacity: 0.6,
                marginTop: isMobile ? 8 : 0,
              }}
            />

            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 18px 12px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {/* Icon — gradient background matching --gradient-primary */}
                <motion.div
                  initial={{ scale: 0.7, rotate: -15 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", damping: 12, stiffness: 200, delay: 0.05 }}
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    background: "var(--gradient-primary)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    flexShrink: 0,
                  }}
                >
                  ✦
                </motion.div>

                <div>
                  {/* Space Grotesk heading font from design system */}
                  <p
                    style={{
                      margin: 0,
                      fontSize: 13,
                      fontWeight: 600,
                      fontFamily: "'Space Grotesk', sans-serif",
                      letterSpacing: "-0.02em",
                      color: "hsl(var(--foreground))",
                    }}
                  >
                    Secret Menu
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 11,
                      color: "hsl(var(--muted-foreground))",
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    You found a hidden zone
                  </p>
                </div>
              </div>

              {/* Close button — matches glass-hover pattern */}
              <button
                onClick={onClose}
                style={{
                  background: "hsl(var(--muted) / 0.5)",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 8,
                  padding: "5px 7px",
                  cursor: "pointer",
                  color: "hsl(var(--muted-foreground))",
                  display: "flex",
                  alignItems: "center",
                  transition: "border-color 0.15s, color 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "hsl(var(--glass-border-hover))";
                  e.currentTarget.style.color = "hsl(var(--foreground))";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "hsl(var(--border))";
                  e.currentTarget.style.color = "hsl(var(--muted-foreground))";
                }}
              >
                <X size={13} />
              </button>
            </div>

            {/* Divider — matches border token */}
            <div style={{ height: 1, background: "hsl(var(--border))", margin: "0 18px" }} />

            {/* Menu items */}
            <div style={{ padding: "10px 10px 14px" }}>
              {menuItems.map((item, i) => {
                const Icon = item.icon;
                return (
                  <motion.button
                    key={item.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.08 + i * 0.06, duration: 0.25 }}
                    onClick={() => {
                      vibrate([40, 20, 80]);
                      onNavigate(item.id);
                      onClose();
                    }}
                    whileTap={{ scale: 0.98 }}
                    style={{
                      width: "100%",
                      background: "transparent",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "calc(var(--radius, 12px) - 2px)",
                      padding: "13px 14px",
                      marginBottom: 6,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      textAlign: "left",
                      transition: "background 0.18s, border-color 0.18s, box-shadow 0.18s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = item.bgHover;
                      e.currentTarget.style.borderColor = item.borderHover;
                      e.currentTarget.style.boxShadow = `0 0 16px ${item.iconBg}`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.borderColor = "hsl(var(--border))";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    {/* Icon bubble */}
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background: item.iconBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon size={16} style={{ color: item.iconColor }} />
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: 13,
                          fontWeight: 600,
                          fontFamily: "'Space Grotesk', sans-serif",
                          letterSpacing: "-0.01em",
                          color: "hsl(var(--foreground))",
                        }}
                      >
                        {item.label}
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: 11,
                          color: "hsl(var(--muted-foreground))",
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        {item.sub}
                      </p>
                    </div>

                    <ChevronRight
                      size={13}
                      style={{ color: "hsl(var(--muted-foreground))", flexShrink: 0 }}
                    />
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}